import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PlacingStatusBadge from "./PlacingStatusBadge";
import PartyName from "./PartyName";
import NotesField from "./NotesField";
import { ReasonSelect, ReasonText } from "./ReasonFields";
import { useReasonCodes } from "../../hooks/useReasonCodes";
import { LineTypeBadge, RoleBadge, StandsBadge } from "./LineBadges";
import { declineLineRequest, getOffer, linkedIds, sendOffer } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import {
  formatDate,
  formatShareRange,
  isInFuture,
  isPast,
  readOffer,
  readParticipation,
  shareFields,
  toIsoUtc,
} from "./placingUtils";

// Offers can be sent (more than one) while the line request is open
const OPEN_STATUSES = ["sent", "offered"];
const MAX_REASON = 2000;

const Field = ({ label, children }) => (
  <Box>
    <Text fontSize="xs" color="gray.500">{label}</Text>
    <Box fontSize="sm">{children}</Box>
  </Box>
);

const lineFormFor = (lineRequest) => ({
  writtenShare: readParticipation(lineRequest).requestedShare != null ? String(readParticipation(lineRequest).requestedShare) : "",
  lineToStand: false,
  conditions: "",
  reference: "",
  stampReference: "",
  validUntilDate: "",
  participationBasis: "",
  currency: "",
  notes: "",
});

/** Carrier view of a line request (Order): stamp and send lines (offers), or decline. */
const CarrierLineRequestView = ({ lineRequest, onChanged }) => {
  const toast = useToast();
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(() => lineFormFor(lineRequest));
  const [sending, setSending] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const { isOther } = useReasonCodes();
  const [declining, setDeclining] = useState(false);

  const role = lineRequest.receiver?.roleCode;
  const terms = readParticipation(lineRequest);
  const offerIds = linkedIds(lineRequest.offers);
  const offerKey = offerIds.join(",");
  const canOffer = OPEN_STATUSES.includes(lineRequest.status);
  const canDecline = lineRequest.status === "sent" && offerIds.length === 0;
  const overdue = lineRequest.status === "sent" && isPast(lineRequest.responseRequiredByDate);
  const isConditional = form.conditions.trim().length > 0;
  const declineReason = lineRequest.receiver?.declineReason || lineRequest.receiver?.responseNotes;

  useEffect(() => {
    let cancelled = false;
    const ids = offerKey ? offerKey.split(",") : [];
    Promise.all(ids.map((id) => getOffer(id)))
      .then((list) => {
        const time = (v) => new Date(v?.$date ?? v ?? 0).getTime();
        if (!cancelled) setOffers(list.sort((a, b) => time(b.stampedDate) - time(a.stampedDate)));
      })
      .catch((err) => !cancelled && showApiErrorToast(toast, err, { title: "Couldn't load your lines" }));
    return () => {
      cancelled = true;
    };
  }, [offerKey, toast]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const stampAndSend = async () => {
    const share = form.writtenShare === "" ? null : Number(form.writtenShare);
    if (share != null && (Number.isNaN(share) || share <= 0 || share > 100)) {
      return toast({ title: "Written share must be more than 0 and at most 100", status: "warning" });
    }
    if (form.validUntilDate && !isInFuture(form.validUntilDate)) {
      return toast({ title: "Valid until must be in the future", status: "warning" });
    }
    setSending(true);
    try {
      // No organisationOwnerId: the API takes it from the line request
      await sendOffer(lineRequest._id, {
        ...shareFields({ writtenShare: form.writtenShare }),
        lineToStand: form.lineToStand,
        ...(isConditional && { conditions: form.conditions.trim() }),
        ...(form.reference && { reference: form.reference }),
        ...(form.stampReference && { stampReference: form.stampReference }),
        ...(form.validUntilDate && { validUntilDate: toIsoUtc(form.validUntilDate) }),
        ...(form.participationBasis && { participationBasis: form.participationBasis }),
        ...(form.currency && { currency: form.currency }),
        ...(form.notes.trim() && { notes: form.notes.trim() }),
      });
      toast({ title: isConditional ? "Conditional line stamped and sent" : "Line stamped and sent", status: "success" });
      setForm(lineFormFor(lineRequest));
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't send line" });
    } finally {
      setSending(false);
    }
  };

  const decline = async () => {
    if (!reasonCode) return toast({ title: "Choose a reason for declining", status: "warning" });
    if (isOther(reasonCode) && !reason.trim()) return toast({ title: "Explain the reason (you chose other)", status: "warning" });
    if (!window.confirm("Decline this line request? You can't send a line on it afterwards.")) return;
    setDeclining(true);
    try {
      await declineLineRequest(lineRequest._id, { reasonCode, reason: reason.trim() });
      toast({ title: "Line request declined", status: "success" });
      setReason("");
      setReasonCode("");
      await onChanged();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't decline" });
    } finally {
      setDeclining(false);
    }
  };

  return (
    <VStack align="stretch" spacing={6}>
      {lineRequest.status === "declined" && (
        <Alert status="error" variant="left-accent">
          <AlertIcon />
          <Box>
            <AlertTitle>You declined this line request</AlertTitle>
            <ReasonText fontSize="sm" code={lineRequest.receiver?.reasonCode} notes={declineReason} />
          </Box>
        </Alert>
      )}
      {lineRequest.status === "ntu" && (
        <Alert status="warning" variant="left-accent">
          <AlertIcon />
          <Box>
            <AlertTitle>Not taken up</AlertTitle>
            <ReasonText fontSize="sm" code={lineRequest.ntuReasonCode} notes={lineRequest.ntuNotes} />
          </Box>
        </Alert>
      )}

      <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <HStack justify="space-between" mb={3}>
          <HStack spacing={3}>
            <Heading size="md">Line request</Heading>
            <RoleBadge role={role} fontSize="md" px={3} />
          </HStack>
          <PlacingStatusBadge status={lineRequest.status} fontSize="md" />
        </HStack>
        <SimpleGrid columns={[1, 2, 4]} spacing={3}>
          <Field label="Broker"><PartyName partyId={lineRequest.sender?.partyId} /></Field>
          <Field label="To"><PartyName partyId={lineRequest.receiver?.partyId} /></Field>
          <Field label="Requested share">{terms.requestedShare != null ? `${terms.requestedShare}%` : "—"}</Field>
          <Field label="Range">{formatShareRange(terms.minimumShare, terms.maximumShare) || "—"}</Field>
          <Field label="Participation basis">{terms.participationBasis || "—"}</Field>
          <Field label="Currency">{terms.currency || "—"}</Field>
          <Field label="Response required by">
            {formatDate(lineRequest.responseRequiredByDate) || "—"}
            {overdue && <Badge ml={2} colorScheme="red">OVERDUE</Badge>}
          </Field>
          {lineRequest.documentId && (
            <Field label="Contract document">
              <Link as={RouterLink} color="blue.600" to={`/editor?doc=${lineRequest.documentId}`}>Open document</Link>
            </Field>
          )}
        </SimpleGrid>
        {lineRequest.notes && (
          <Box mt={3}>
            <Text fontSize="xs" color="gray.500">Notes from the broker</Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">{lineRequest.notes}</Text>
          </Box>
        )}
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <Heading size="sm" mb={3}>Your lines</Heading>
        {offers.length === 0 ? (
          <Text fontSize="sm" color="gray.500">You haven't sent a line yet.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Status</Th><Th>Written</Th><Th>Line</Th><Th>Reference</Th><Th>Stamp</Th><Th>Valid until</Th><Th>Line detail</Th><Th>Stamped</Th></Tr>
            </Thead>
            <Tbody>
              {offers.map((offer) => {
                const line = readOffer(offer);
                const expired = isPast(offer.validUntilDate);
                return (
                  <Tr key={offer._id}>
                    <Td>
                      <PlacingStatusBadge status={offer.status} />
                      {offer.status === "ntu" && <ReasonText color="orange.700" mt={1} code={offer.ntuReasonCode} notes={offer.ntuNotes} />}
                    </Td>
                    <Td fontWeight="semibold">{line.writtenShare != null ? `${line.writtenShare}%` : "—"}</Td>
                    <Td>
                      <HStack spacing={1}><LineTypeBadge lineType={line.lineType} />{line.lineToStand && <StandsBadge />}</HStack>
                      {line.conditions && <Text fontSize="xs" color="orange.700" whiteSpace="pre-wrap" mt={1}>{line.conditions}</Text>}
                      {offer.conditionsAccepted && <Badge colorScheme="green" variant="outline" mt={1}>Conditions accepted</Badge>}
                    </Td>
                    <Td>{offer.reference || "—"}</Td>
                    <Td>{offer.stampReference || "—"}</Td>
                    <Td>
                      {formatDate(offer.validUntilDate) || "—"}
                      {expired && <Badge ml={2} colorScheme="red">EXPIRED</Badge>}
                    </Td>
                    <Td maxW="320px" whiteSpace="pre-wrap" fontSize="xs">{offer.notes || ""}</Td>
                    <Td>{formatDate(offer.stampedDate)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      {canOffer && (
        <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
          <Heading size="sm" mb={1}>{offers.length ? "Send another line" : "Send a line"}</Heading>
          {/* The API takes the offer's organisation from the line request */}
          <Text fontSize="sm" color="gray.600" mb={3}>
            Writing as <Text as="span" fontWeight="semibold"><PartyName partyId={lineRequest.receiver?.partyId} /></Text>
            {role && <> · <RoleBadge role={role} /></>}
          </Text>
          <SimpleGrid columns={[1, 3]} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs">Written share %</FormLabel>
              <Input size="sm" type="number" min={0} max={100} step="any" value={form.writtenShare} onChange={set("writtenShare")} />
              <FormHelperText fontSize="xs">Pre-filled from the request</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Valid until (optional)</FormLabel>
              <Input size="sm" type="datetime-local" value={form.validUntilDate} onChange={set("validUntilDate")} />
            </FormControl>
            <FormControl display="flex" alignItems="flex-start" flexDirection="column">
              <FormLabel fontSize="xs">Line to stand</FormLabel>
              <Checkbox isChecked={form.lineToStand} onChange={(e) => setForm((f) => ({ ...f, lineToStand: e.target.checked }))}>
                <Text fontSize="sm">This line must stand: it won't be signed down</Text>
              </Checkbox>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Your reference</FormLabel>
              <Input size="sm" value={form.reference} onChange={set("reference")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Stamp reference</FormLabel>
              <Input size="sm" placeholder="Syndicate / company stamp" value={form.stampReference} onChange={set("stampReference")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Participation basis</FormLabel>
              <Input size="sm" placeholder={terms.participationBasis || "As requested"} value={form.participationBasis} onChange={set("participationBasis")} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Currency</FormLabel>
              <Input size="sm" placeholder={terms.currency || "As requested"} value={form.currency} onChange={set("currency")} />
            </FormControl>
          </SimpleGrid>
          <FormControl mt={3}>
            <HStack mb={1} spacing={2}>
              <FormLabel fontSize="xs" mb={0}>Conditions</FormLabel>
              <LineTypeBadge lineType={isConditional ? "conditional_line" : "unconditional_line"} />
              <Text fontSize="xs" color="gray.500">{isConditional ? "Will be sent as a conditional line" : "No conditions: an unconditional line"}</Text>
            </HStack>
            <Textarea size="sm" value={form.conditions} onChange={set("conditions")} placeholder="Any conditions make this a conditional line" />
          </FormControl>
          <NotesField mt={3} label="Line detail" rows={8} value={form.notes} onChange={(notes) => setForm((f) => ({ ...f, notes }))} />
          <HStack mt={3}>
            <Button size="sm" colorScheme={isConditional ? "orange" : "blue"} onClick={stampAndSend} isLoading={sending}>
              {isConditional ? "Stamp and send conditional line" : "Stamp and send line"}
            </Button>
          </HStack>
        </Box>
      )}

      {canDecline && (
        <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
          <Heading size="sm" mb={3}>Decline</Heading>
          <ReasonSelect isRequired value={reasonCode} onChange={setReasonCode} mb={3} />
          <FormControl isRequired={isOther(reasonCode)}>
            <FormLabel fontSize="xs">{isOther(reasonCode) ? "Explain the reason" : "More detail (optional)"}</FormLabel>
            <Textarea size="sm" value={reason} maxLength={MAX_REASON} onChange={(e) => setReason(e.target.value)} />
            <FormHelperText fontSize="xs">{reason.length} / {MAX_REASON}</FormHelperText>
          </FormControl>
          <HStack mt={3}>
            <Button size="sm" colorScheme="red" onClick={decline} isLoading={declining}>Decline line request</Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

export default CarrierLineRequestView;
